import React, { useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTheme } from '../../../ThemeContext.tsx';
import { CategoryData } from './types';

interface WelcomeCardProps {
    card: CategoryData;
    onQuestionSelect: (question: string) => void;
    defaultExpanded?: boolean;
}

const WelcomeCard: React.FC<WelcomeCardProps> = ({ card, onQuestionSelect, defaultExpanded = false }) => {
    const { colors, theme, isMobile } = useTheme();
    const [isExpanded, setIsExpanded] = useState(true);

    useEffect(() => {
        // On mobile, only keep expanded if defaultExpanded is true (first card)
        // On desktop, always expand
        setIsExpanded(isMobile ? defaultExpanded : true);
    }, [isMobile, defaultExpanded]);

    return (
        <div
            className={`
                ${theme === 'light' ? card.bgColor : 'bg-white/5'} 
                rounded-xl shadow p-3 sm:p-4 
                flex flex-col gap-2 
                border ${colors.border.primary} 
                w-full
                ${isMobile ? 'min-h-0' : 'h-full min-h-[180px] max-h-[330px]'}
            `}
        >
            {/* Header */}
            <div className="flex items-center justify-between gap-2 flex-shrink-0">
                <div className="flex items-center gap-2">
                    <div className={card.iconColor}>
                        {card.icon}
                    </div>
                    <span className={`font-semibold text-sm sm:text-base ${colors.text.primary} whitespace-nowrap`}>
                        {card.title}
                    </span>
                </div>
                {isMobile && (
                    <button
                        onClick={() => setIsExpanded(prev => !prev)}
                        aria-expanded={isExpanded}
                        className={`p-1 rounded-md ${colors.interactive.secondaryHover} transition-transform`}
                    >
                        <ChevronDown
                            size={18}
                            className={`text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : 'rotate-0'}`}
                        />
                    </button>
                )}
            </div>

            {/* Description */}
            <p className={`
                text-xs font-medium text-gray-500 
                text-left ml-1 
                pb-1 border-b ${colors.border.secondary} 
                mb-0.5
                flex-shrink-0
            `}>
                {card.description}
            </p>

            {/* Questions */}
            {(!isMobile || isExpanded) && (
                <div 
                    className={`flex flex-col gap-1.5 ${isMobile ? '' : 'flex-1 min-h-0 overflow-y-auto'} pr-1`}
                    style={{
                        scrollbarWidth: 'thin',
                        scrollbarColor: 'rgb(156 163 175) transparent'
                    }}
                >
                    {card.questions.map((question, index) => (
                        <button
                            key={index}
                            onClick={() => onQuestionSelect(question)}
                            className={`
                                w-full
                                text-left
                                bg-white
                                ${colors.bg.primary}/90
                                ${colors.interactive.secondaryHover} 
                                transition-colors 
                                rounded-md 
                                p-2 sm:p-1.5
                                text-xs sm:text-sm
                                font-medium 
                                ${colors.text.primary}
                                break-words
                                whitespace-normal
                                hover:opacity-90
                                active:opacity-80
                                touch-pan-y
                            `}
                            style={{
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word'
                            }}
                        >
                            {question}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default WelcomeCard;
